import { SagaIterator } from 'redux-saga';
import { put, select, call, all, take } from 'redux-saga/effects';
import {
  errorsGlobalError,
  changeModalState,
  showNotifyFail,
  showNotifySuccess,
  notify
} from '../../actions';
import { getSubscription } from '../../selectors/subscription';
import { get } from 'lodash';
import queryString from 'query-string';
import { SUBSCRIPTION_INTERVALS } from '../../constants';
import { Api, getTranslationKey } from '../../utils';
import ApiErrors from '../../utils/API/APIErrors';
import { isPaymentMethodSetupRequiredError } from '../../utils/API/paymentMethodSetupRequired';
import { savePendingPaymentSetupAction } from '../../utils/pendingPaymentSetupAction';
import { AnyAction } from 'redux';
import { fetchPaymentInfoAction, putPaymentInfoAction } from '../../actions/paymentInfo';
import { updateSubscriptionStore } from '../../actions/subscription';
import { IRestartSubscriptionActionSource, ISubscriptionItem } from '../../types/subscriptions';
import { getPaymentInfo } from '../../selectors/paymentInfo';
import { ISagaAction } from '../../types';
import { ISetupIntentFailed } from '../../types/subscription';
import { getUser } from '../../selectors';
import { getCurrentUser } from '../../../actions';

export const buySubscriptionSaga = function* (): SagaIterator {
  try {
    yield put(updateSubscriptionStore({ isRedirecting: false }));
    const subscription = yield select(getSubscription);
    const { plan, billingCycle, brandsAmount, domainsAmount, templates, videoStorage, slideshows } =
      subscription;
    const interval = get(SUBSCRIPTION_INTERVALS, billingCycle, '');
    const linkPath = `${plan}/${interval}`;
    const querySymbol = templates || videoStorage || slideshows ? '?' : '';
    const data: any = {};
    if (templates) {
      data.templates = templates;
    }
    if (videoStorage) {
      data.videoStorage = videoStorage;
    }
    if (brandsAmount) {
      data.brandsAmount = brandsAmount;
    }
    if (domainsAmount) {
      data.domainsAmount = domainsAmount;
    }
    if (slideshows) {
      data.slideshowsPlan = 'unlimited';
    }

    const query = queryString.stringify(data, { arrayFormat: 'bracket' });
    localStorage.setItem('subscriptionConfig', JSON.stringify(subscription));

    const res = yield call(Api.User.getBuyUrl, { linkPath, querySymbol, query });
    ApiErrors.checkOnApiError(res);

    // just until dgigistore fixed the pop up bug
    yield put(updateSubscriptionStore({ isRedirecting: true }));
    window.location.href = res.url;
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const cancelSubscriptionSaga = function* (actions: AnyAction): SagaIterator {
  try {
    yield call(Api.User.cancelSubscription, actions.payload);

    yield put(fetchPaymentInfoAction());
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const addPromotionCodeToSubscriptionSaga = function* (actions: AnyAction): SagaIterator {
  try {
    const response = yield call(Api.User.addPromotionCodeToSubscription, actions.payload);

    if (isPaymentMethodSetupRequiredError(response)) {
      savePendingPaymentSetupAction({
        kind: 'redux-action',
        actionType: 'addPromotionCode',
        payload: actions.payload
      });
      yield put(changeModalState({ key: 'paymentMethodSetupRequiredModal', state: true }));

      return;
    }

    window.location.href = 'https://scrappbook.de/treue-kunden-danke';

    yield put(fetchPaymentInfoAction());
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const restartSubscriptionSaga = function* (actions: AnyAction): SagaIterator {
  try {
    const { subscriptions, paymentProvider } = yield select(getPaymentInfo);
    const {
      payload: { subscriptionType, actionSource }
    } = actions;

    const response = yield call(Api.User.restartSubscription, {
      subscriptionType
    });

    if (isPaymentMethodSetupRequiredError(response)) {
      savePendingPaymentSetupAction({
        kind: 'redux-action',
        actionType: 'restartSubscription',
        payload: actions.payload
      });
      yield put(changeModalState({ key: 'activeCancelledSubModal', state: false }));
      yield put(changeModalState({ key: 'paymentMethodSetupRequiredModal', state: true }));

      return;
    }

    const { billing_status, rebilling_start_url, result } = response;

    if (paymentProvider !== 'stripe' && billing_status !== 'paying') {
      if (rebilling_start_url) {
        window.open(rebilling_start_url, '_blank');

        return;
      }

      const subSelected = subscriptions.find(
        (subscription: ISubscriptionItem) => subscription.subscriptionType === subscriptionType
      );

      if (subSelected.renewUrl) {
        yield put(
          changeModalState({
            key: 'activeCancelledSubModal',
            state: false
          })
        );
        yield put(
          changeModalState({
            key: 'restartSubErrorModal',
            state: true,
            modalData: {
              renewUrl: subSelected.renewUrl
            }
          })
        );

        return;
      }

      throw result;
    }

    yield put(
      changeModalState({
        key: 'activeCancelledSubModal',
        state: false
      })
    );
    yield put(showNotifySuccess({}));
    yield put(fetchPaymentInfoAction());

    if (
      paymentProvider !== 'stripe' &&
      actionSource === IRestartSubscriptionActionSource.ActiveCancelledSubModal
    ) {
      yield call(buySubscriptionSaga);
    }
  } catch (e) {
    yield put(showNotifyFail());
    yield put(errorsGlobalError(e as ErrorEvent));
    yield put(
      changeModalState({
        key: 'activeCancelledSubModal',
        state: false
      })
    );
  }
};

export const updateBillingAddressSaga = function* (actions: AnyAction): SagaIterator {
  try {
    const billingAddpayload = actions.payload;
    const location = billingAddpayload.routerPath;
    const { justUpdate, silentSuccess } = billingAddpayload;
    const isTaxSettingsModal = billingAddpayload?.isTaxSettingsModal;
    const closeForm = billingAddpayload?.closeForm;

    if (billingAddpayload.waitForTaxSettingsUpdate) {
      yield take('SET_USER_TAX_SETTINGS');
    }

    const locationsAllowedToSetApi = [
      '/subscription',
      '/subscription/pricing/payment',
      '/shop/payment-settings'
    ];

    // Clean payload with billing address fields, converting null/undefined to empty strings
    // Handles all user cases: new users, users with partial info, users with Stripe data only, etc.
    const billingFields = [
      'email',
      'country',
      'firstName',
      'lastName',
      'company',
      'street',
      'streetNumber',
      'zipcode',
      'city'
    ];

    const cleanPayload: Record<string, string | boolean> = Object.fromEntries(
      billingFields.map((field) => [field, billingAddpayload[field] ?? ''])
    );

    if (locationsAllowedToSetApi.includes(location) || isTaxSettingsModal) {
      cleanPayload.setApi = true;
    }

    if (!navigator.onLine) {
      yield put(showNotifyFail({ message: getTranslationKey('offlineMessage') }));
      yield put({ type: 'BILLING_ADDRESS_UPDATE_FAILED' });

      return;
    }

    let paymentInfo = yield select(getPaymentInfo);
    yield put(putPaymentInfoAction({ ...paymentInfo, updatingAddress: true }));

    const res = yield call(Api.User.updateBillingAddress, cleanPayload);

    paymentInfo = yield select(getPaymentInfo);
    yield put(putPaymentInfoAction({ ...paymentInfo, updatingAddress: false }));

    if (res.code === 0) {
      yield put(showNotifyFail({ message: getTranslationKey('offlineMessage') }));
      yield put({ type: 'BILLING_ADDRESS_UPDATE_FAILED' });

      return;
    }

    if (!res.error) {
      // After a country change the previously saved account currency may no
      // longer be offered (e.g. CHF after switching away from Switzerland).
      try {
        const newCountry = cleanPayload.country as string;
        const currentUser = yield select(getUser);

        if (newCountry && currentUser.currency) {
          const currenciesRes = yield call(Api.User.getAvailableCurrencies, newCountry);
          const availableCurrencies: string[] = currenciesRes?.result?.currencies ?? [];

          if (availableCurrencies.length && !availableCurrencies.includes(currentUser.currency)) {
            yield call(
              Api.User.updateUserByID,
              { currency: availableCurrencies[0] },
              currentUser._id
            );
          }
        }
      } catch (currencyError) {
        yield put(errorsGlobalError(currencyError as ErrorEvent));
      }

      const effects = [
        put(fetchPaymentInfoAction()),
        // @ts-ignore
        yield put(getCurrentUser())
      ];

      if (!silentSuccess) {
        effects.push(
          put(
            notify({
              title: getTranslationKey('success'),
              status: 'success',
              position: 'top-center',
              dismissible: true,
              dismissAfter: 7500
            })
          )
        );
      }

      yield all(effects);

      // Signal that billing address update is complete
      yield put({ type: 'BILLING_ADDRESS_UPDATE_COMPLETED' });

      if (closeForm) closeForm();

      if (justUpdate || paymentInfo.paymentProvider === 'stripe') return;

      if (location !== '/subscription') {
        yield call(buySubscriptionSaga);
      } else {
        yield put(changeModalState({ key: 'billingAddressChangeModal', state: true }));
      }
    } else {
      throw res.error || res.response.data.error;
    }
  } catch (e) {
    yield put({ type: 'BILLING_ADDRESS_UPDATE_FAILED' });
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const setupIntentFailed = function* ({
  payload
}: ISagaAction<ISetupIntentFailed>): SagaIterator {
  try {
    const response = yield call(Api.Subscription.setupIntentFailed, payload);
    ApiErrors.checkOnApiError(response);

    const user = yield select(getUser);
    yield put({
      type: 'SET_USER',
      payload: {
        subscription: {
          ...user.subscription,
          state: 'payment_setup_problem',
          stripe: {
            ...user.subscription.stripe,
            paymentSetup: {
              clientSecret: payload.clientSecret,
              returnUrl: payload.returnUrl
            }
          }
        }
      }
    });
  } catch (e) {
    console.error('Failed to track event', e);
  }
};
